import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import CountdownTimer from '../../DynamicMemberPortal/components/CountdownTimer/CountdownTimer';
import { useNetwork } from '../../../../utils/network';
import { useUser } from '../../../../contexts/UserContext';
import { pendingDrawsLine } from '../../../../utils/pendingDraws';

type GiveawayNotificationProps = {
  showHeader?: boolean;
};

// Arena palette — mirrors the dashboard cards / the Giveaway hero so this block
// reads as part of the same system (was flat MUI `divider` boxes + teal accents).
const C = {
  cyan: '#38e8ff',
  gold: '#ffcf40',
  green: '#42e6a4',
  text: '#e8f4fb',
  dim: '#8ea3b3',
  dimmer: '#5c6b78',
  mono: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace'
};

// Shared arena panel — dark gradient, cyan hairline, soft corner glow, fills its
// grid cell so the two cards sit at equal height.
const panelSx = {
  position: 'relative',
  overflow: 'hidden',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  p: { xs: 2.5, md: 3.5 },
  borderRadius: '16px',
  border: '1px solid rgba(56, 232, 255, 0.22)',
  background:
    'linear-gradient(180deg, rgba(11, 17, 25, 0.74), rgba(7, 11, 17, 0.78))',
  boxShadow: '0 18px 40px -26px rgba(0, 0, 0, 0.9)',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: '0 auto auto 0',
    width: 320,
    height: 180,
    pointerEvents: 'none',
    background:
      'radial-gradient(420px 220px at 0 0, rgba(56, 232, 255, 0.08), transparent 65%)'
  }
} as const;

const kickSx = {
  font: `800 10px ${C.mono}`,
  letterSpacing: '0.32em',
  textTransform: 'uppercase',
  color: C.gold
} as const;

export const GiveawayNotification = ({
  showHeader = true
}: GiveawayNotificationProps) => {
  const { get } = useNetwork();
  const [nextGiveaway, setNextGiveaway] = useState(new Date());
  const [prizeLabel, setPrizeLabel] = useState('A$10,000');
  const { user } = useUser();

  // Prefix A$ (not the bare "$" the en-AU currency style produces) so the fetched
  // value matches the initial label — otherwise it flashed "A$10,000" then reverted
  // to "$10,000" on load, and it wasn't clear the prize is in AUD.
  const formatPrizeLabel = (value?: number | string | null) => {
    const amount = Number(value);
    const safeAmount = Number.isFinite(amount) && amount > 0 ? amount : 10000;
    return `A$${safeAmount.toLocaleString('en-AU', {
      maximumFractionDigits: 0
    })}`;
  };

  useEffect(() => {
    const fetchGiveawayCountdown = async () => {
      try {
        const data = await get('/giveaway/countdown', true);
        setNextGiveaway(new Date(data.endDate));
        setPrizeLabel(formatPrizeLabel(data.prizeAmount));
      } catch (error) {
        console.error('There was a problem with the network operation:', error);
      }
    };
    fetchGiveawayCountdown();
  }, []);

  const entries = user.isGiveawayEligible ? user?.entries ?? 0 : 'N/A';

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
        gap: 2
      }}
    >
      {/* ---- upcoming draw + countdown ---- */}
      <Box sx={panelSx}>
          <Typography sx={kickSx}>Upcoming Giveaway</Typography>
          <Typography
            sx={{
              mt: 1.5,
              fontWeight: 900,
              fontSize: 'clamp(30px, 4.4vw, 48px)',
              lineHeight: 1.02,
              letterSpacing: '-0.02em',
              color: C.text
            }}
          >
            <Box
              component="span"
              sx={{
                color: C.cyan,
                textShadow: '0 0 30px rgba(56, 232, 255, 0.35)'
              }}
            >
              {prizeLabel}
            </Box>{' '}
            in INC
          </Typography>
          <Typography
            sx={{ mt: 1.25, font: `600 13px/1.55 ${C.mono}`, color: C.dim }}
          >
            Don&apos;t miss your shot at the jackpot draw — closes in:
          </Typography>

          <Box sx={{ mt: 3, mb: showHeader ? 2.5 : 0 }}>
            <CountdownTimer nextGiveaway={nextGiveaway} isJumbo />
          </Box>

          {showHeader && (
            <Typography
              sx={{
                mt: 'auto',
                textAlign: 'center',
                font: `600 12px/1.6 ${C.mono}`,
                color: C.dimmer
              }}
            >
              Buy packs below to go into the draw — more entries, more chances to
              win.
            </Typography>
          )}
      </Box>

      {/* ---- entries ---- */}
      <Box sx={{ ...panelSx, textAlign: 'center', alignItems: 'center' }}>
          <Typography sx={{ ...kickSx, color: C.dim, alignSelf: 'center' }}>
            Your Entries
          </Typography>
          <Typography
            sx={{
              mt: 1,
              font: `700 11px ${C.mono}`,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: C.dimmer,
              maxWidth: 260
            }}
          >
            in the {prizeLabel} giveaway
          </Typography>
          <Typography
            sx={{
              mt: 'auto',
              mb: user.hasActiveSubscription ? 0 : 'auto',
              fontSize: 'clamp(56px, 9vw, 92px)',
              fontWeight: 900,
              lineHeight: 1,
              color: C.cyan,
              fontVariantNumeric: 'tabular-nums',
              textShadow: '0 0 40px rgba(56, 232, 255, 0.3)'
            }}
          >
            {typeof entries === 'number'
              ? entries.toLocaleString('en-AU')
              : entries}
          </Typography>
          {user.isGiveawayEligible && pendingDrawsLine(user.pendingJackpot) && (
            <Typography
              sx={{
                mt: 1,
                font: `700 10px ${C.mono}`,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: C.gold,
                maxWidth: 280
              }}
            >
              {pendingDrawsLine(user.pendingJackpot)}
            </Typography>
          )}

          {user.hasActiveSubscription && (
            <Box
              sx={{
                mt: 3,
                pt: 2.5,
                width: 'min(92%, 340px)',
                borderTop: '1px solid rgba(56, 232, 255, 0.14)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}
            >
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  px: 1.25,
                  py: 0.5,
                  borderRadius: '999px',
                  border: `1px solid ${C.green}66`,
                  background: 'rgba(66, 230, 164, 0.08)',
                  font: `800 9px ${C.mono}`,
                  letterSpacing: '0.16em',
                  textTransform: 'uppercase',
                  color: C.green
                }}
              >
                <Box
                  component="span"
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    background: C.green,
                    boxShadow: `0 0 8px ${C.green}`
                  }}
                />
                Weekly $1,000 draws
              </Box>
              <Typography
                sx={{
                  mt: 1.25,
                  fontSize: { xs: 44, md: 56 },
                  fontWeight: 900,
                  lineHeight: 1,
                  color: C.green,
                  fontVariantNumeric: 'tabular-nums',
                  textShadow: '0 0 30px rgba(66, 230, 164, 0.3)'
                }}
              >
                {user.isGiveawayEligible
                  ? (user.stackingEntries ?? 0).toLocaleString('en-AU')
                  : 'N/A'}
              </Typography>
              <Typography
                sx={{
                  mt: 0.5,
                  font: `700 9px ${C.mono}`,
                  letterSpacing: '0.16em',
                  textTransform: 'uppercase',
                  color: C.dimmer
                }}
              >
                Stacking entries
              </Typography>
            </Box>
          )}
      </Box>
    </Box>
  );
};
